import { FC, useCallback, useState } from 'react';
import { useRouter } from 'shared-routing';
import { AccommodationDetailTemplate, DetailModal } from 'voyager';
import type { JourneyPickerSearchParams } from 'voyager';

import { ContentParser } from '@common/components/ContentParser';
import { Routes } from '@common/constants';
import { DynamicPackagesConfigurationWizard } from '@features/DynamicPackagesConfigurationWizard';
import { UpgradeItemVersion } from '@features/List/BookableList/UpgradeItem';
import { RoomRateConfigurationWizard } from '@features/RoomRateConfigurationWizard';
import { isInlineBookingStep } from '@features/RoomRateConfigurationWizard/FormSteps/constants';
import { useLoadingState } from '@common/hooks';
import useDetailModalLabels from '@common/hooks/useDetailModalLabels';
import { useRatesErrorMessage } from '@common/hooks/useRatesErrorMessage';
import useRoomCapacityText from '@common/hooks/useRoomCapacityText';
import { analytics } from '@features/analytics';
import { JourneyPickerV2Connected } from '@features/JourneyPickerV2/container';
import { openJourneyPickerDates } from '@features/JourneyPickerV2/openJourneyPickerDates';
import { useJourneyPickerAvailability } from '@features/JourneyPickerV2/useJourneyPickerAvailability';
import { formatCheckInOutLine } from '@features/rates/helpers/formatCheckInOutLine.helper';
import { moreRoomsAvailableThanInBasket } from '@features/rates/helpers/moreRoomsAvailableThanInBasket.helper';
import { useDenialAnalytics } from '@features/rates/helpers/useDenialAnalytics.helper';
import { useRoomSettings } from '@features/roomSettings';
import { useTranslations } from '@lang';
import { parseDateToString } from '@utils/helpers';
import { queryParamToString } from '@utils/queryParam';
import rewriteHttp from '@utils/rewriteHttp';

import { RoomUpgrade } from 'shared-types/Rate';

import { RateDetailV2Props } from './RateDetailV2.types';
import { useRateDetail } from './useRateDetail';

export const RateDetailV2: FC<RateDetailV2Props> = ({
  journeyParams,
  changeJourneyParam,
  language,
  property,
  roomIdsInBasket,
  backRoute,
  backText,
}) => {
  const router = useRouter();
  const { t } = useTranslations();

  const bookingStep = queryParamToString(router.query.bookingStep);
  const bookedRoomId = queryParamToString(router.query.room);

  const {
    rateCode,
    rate,
    availableRooms,
    items,
    fallbackImageUrl,
    isPackage,
    isPackageUnavailable,
    isFetching,
    isLoading,
    error,
  } = useRateDetail(journeyParams, roomIdsInBasket, language, property);
  const { isInitialLoading, isRefetching } = useLoadingState(isFetching, !!rate);
  const errorMessage = useRatesErrorMessage(error);

  const [selectedRoomId, setSelectedRoomId] = useState<string | undefined>(undefined);
  const effectiveSelectedId = availableRooms.some((room) => room.id === selectedRoomId)
    ? selectedRoomId
    : availableRooms[0]?.id;

  const [detailRoomId, setDetailRoomId] = useState<string | undefined>(undefined);
  const [isPackageInlineStep, setIsPackageInlineStep] = useState(false);
  const detailRoom = availableRooms.find((room) => room.id === detailRoomId);
  const detailModalLabels = useDetailModalLabels();
  const detailCapacityText = useRoomCapacityText({
    maxAdults: detailRoom?.maxAdults,
    maxChildren: detailRoom?.maxChildren,
    maxOccupancy: detailRoom?.maxOccupancy,
  });

  const { data: roomSettingsData } = useRoomSettings();
  const roomSettings = roomSettingsData?.data;

  const { availability, isLoadingAvailability, onMonthChange } = useJourneyPickerAvailability({
    adults: journeyParams.adults ?? 0,
    children: journeyParams.children ?? 0,
    initialMonth: journeyParams.fromDate?.format('YYYY-MM-DD'),
  });

  const itemCategory = router.pathname === Routes.SPA_STAYS_DETAIL ? 'spa-stays' : 'rates';

  useDenialAnalytics({
    isFetching,
    hasAvailability: availableRooms.length > 0,
    itemCategory,
    itemId: rate?.id,
    itemName: rate?.title ?? '',
    fromDate: journeyParams.fromDate,
    toDate: journeyParams.toDate,
  });

  const handleSearchComplete = useCallback(
    (params: JourneyPickerSearchParams) => {
      const adults = params.adults;
      const children = params.children ?? 0;

      changeJourneyParam('rooms', [{ roomNumber: 1, adults, children }]);
      changeJourneyParam('childAges', []);

      analytics.track({
        event: 'view_item',
        params: {
          adults,
          children,
          start_date: params.fromDate,
          end_date: params.toDate,
          items: [{ item_category: itemCategory, item_id: rateCode, item_name: rate?.title ?? '' }],
        },
      });
    },
    [changeJourneyParam, itemCategory, rateCode, rate?.title]
  );

  const handleEditSearch = useCallback(() => {
    openJourneyPickerDates();
  }, []);

  const handleBack = useCallback(() => {
    router.push({ pathname: backRoute ?? Routes.RATE_LIST });
  }, [router, backRoute]);

  const reserveRoom = useCallback(
    (roomId?: string) => {
      const room = availableRooms.find((availableRoom) => availableRoom.id === roomId);
      if (!room) return;

      const promoRateCode = queryParamToString(router.query.promoRateCode);

      const upgrade = room.roomUpgrade;
      const canOfferUpgrade =
        !isPackage && !!upgrade && moreRoomsAvailableThanInBasket(upgrade, roomIdsInBasket);

      setDetailRoomId(undefined);
      router.push({
        pathname: Routes.RATE_BOOKING,
        query: {
          id: rateCode,
          room: room.id,
          bookingStep: canOfferUpgrade ? 'upgrade' : 'guestDetailsStep',
          fromDate: parseDateToString(journeyParams.fromDate),
          toDate: parseDateToString(journeyParams.toDate),
          spaDate: parseDateToString(journeyParams.fromDate),
          adults: String(journeyParams.adults),
          children: String(journeyParams.children),
          ...(promoRateCode ? { promoRateCode } : undefined),
        },
      });
    },
    [availableRooms, isPackage, journeyParams, rateCode, roomIdsInBasket, router]
  );

  const handleReserve = useCallback(() => {
    reserveRoom(effectiveSelectedId);
  }, [effectiveSelectedId, reserveRoom]);

  const journeyBar = (
    <JourneyPickerV2Connected
      imageOverlay
      dateMode="range"
      dateContext="stay"
      showGuests
      numberOfMonths={1}
      availability={availability}
      isLoadingAvailability={isLoadingAvailability}
      onMonthChange={onMonthChange}
      onSearchComplete={handleSearchComplete}
    />
  );

  const rateImages = (rate?.images ?? []).filter(Boolean);
  const fallbackImages = fallbackImageUrl ? [fallbackImageUrl] : [];
  const gallerySources = rateImages.length ? rateImages : fallbackImages;
  const images = gallerySources.map((src, index) => ({
    src: rewriteHttp(src),
    alt:
      gallerySources.length > 1
        ? `${rate?.title ?? ''} (${index + 1}/${gallerySources.length})`
        : rate?.title ?? '',
  }));

  const checkInOutLine = formatCheckInOutLine(t, roomSettings);

  const descriptionParts = rate
    ? [rate.description, checkInOutLine, rate.cancellationPolicyText].filter(
        (part): part is string => !!part
      )
    : [];
  const description = descriptionParts.length ? descriptionParts : undefined;

  const bookedRoom = rate?.rooms?.find((room) => room.id === bookedRoomId);
  const upgrade = bookedRoom?.roomUpgrade;
  const isUpgradeStep = bookingStep === 'upgrade' && !!upgrade;
  const guestStepLinkParams = { ...router.query, bookingStep: 'guestDetailsStep' };

  if (rate && isUpgradeStep) {
    return (
      <UpgradeItemVersion
        isOpen
        {...(upgrade as RoomUpgrade)}
        linkRoute={Routes.RATE_BOOKING}
        linkParams={guestStepLinkParams}
        upgradeLinkParams={{ ...guestStepLinkParams, room: upgrade?.id }}
        language={language}
        numberOfNights={(
          journeyParams.toDate ?? journeyParams.fromDate.clone().add(1, 'days')
        ).diff(journeyParams.fromDate, 'days')}
        originalRoom={
          bookedRoom
            ? { id: bookedRoom.id, name: bookedRoom.name, imageUrl: bookedRoom.imageUrl }
            : undefined
        }
      />
    );
  }

  const isMultiRoom = (journeyParams.rooms?.length ?? 0) > 1;
  const isV2BookingStep = isInlineBookingStep(bookingStep, isMultiRoom);

  const roomRateWizard =
    rate && !isPackage ? (
      <RoomRateConfigurationWizard
        bookingStep={bookingStep}
        language={language}
        metaEntity={{ type: 'rate', ...rate } as any}
        entity={{ type: 'room', id: bookedRoomId } as any}
      />
    ) : null;

  const isNotFound = !rate && !isLoading && !isFetching;
  const isUnavailable = !!rate && !isFetching && (items.length === 0 || isPackageUnavailable);
  const displayedItems = isFetching || isPackageUnavailable ? [] : items;

  const unavailable = isNotFound
    ? {
        title: t('rate.noResults'),
        ctaLabel: backText ?? t('app.backToRates'),
        onCtaClick: handleBack,
      }
    : isUnavailable
    ? {
        title: error ? errorMessage.title : t('rate.noRoomHeader'),
        subtitle: error ? errorMessage.message : t('rate.noRoomsInfo'),
        ctaLabel: t('common.editSearch'),
        onCtaClick: handleEditSearch,
      }
    : undefined;

  const showDetail = !(isV2BookingStep && roomRateWizard) && !isPackageInlineStep;

  return (
    <>
      {showDetail && (
        <>
          <AccommodationDetailTemplate
            title={rate?.title ?? ''}
            images={images}
            items={displayedItems}
            showItemImages
            selectedItemId={effectiveSelectedId}
            onItemSelect={setSelectedRoomId}
            onSelectItem={setDetailRoomId}
            selectItemLabel={t('common.moreDetails')}
            onReserve={handleReserve}
            reserveLabel={t('common.reserve')}
            showMoreItemsLabel={t('common.showMore')}
            showLessItemsLabel={t('common.showLess')}
            description={description}
            descriptionShowMoreLabel={t('common.showMore')}
            descriptionShowLessLabel={t('common.showLess')}
            editSearchLabel={t('common.editSearch')}
            onEditSearch={handleEditSearch}
            previousImageLabel={t('aria.previousImage')}
            nextImageLabel={t('aria.nextImage')}
            goToImageLabel={(position, total) => t('aria.goToImage', { position, total })}
            onBack={handleBack}
            viewAllLabel={t('common.viewAll')}
            journeyBar={journeyBar}
            isLoading={isInitialLoading || isRefetching}
            unavailable={unavailable}
          />
          {detailRoom && !bookingStep && (
            <DetailModal
              open
              onClose={() => setDetailRoomId(undefined)}
              title={detailRoom.name}
              images={
                detailRoom.imageUrl
                  ? [{ src: rewriteHttp(detailRoom.imageUrl), alt: detailRoom.name }]
                  : []
              }
              tags={detailCapacityText ? [detailCapacityText] : undefined}
              pricePerStay={{
                amount: detailRoom.pricePerStay.amount,
                currencyCode: detailRoom.pricePerStay.currencyCode,
              }}
              pricePerNight={{
                amount: detailRoom.pricePerNight.amount,
                currencyCode: detailRoom.pricePerNight.currencyCode,
              }}
              showTotalDue
              description={
                detailRoom.description ? (
                  <ContentParser text={detailRoom.description} escapeHtml={false} />
                ) : undefined
              }
              amenities={(detailRoom.amenities ?? []).map((amenity) => ({
                id: amenity,
                label: amenity,
              }))}
              terms={
                rate?.cancellationPolicyText ? (
                  <ContentParser text={rate.cancellationPolicyText} escapeHtml={false} />
                ) : undefined
              }
              onReserve={() => reserveRoom(detailRoom.id)}
              locale={language}
              labels={detailModalLabels}
            />
          )}
        </>
      )}
      {roomRateWizard}
      {rate && isPackage && bookingStep && (
        <DynamicPackagesConfigurationWizard
          language={language}
          rate={{ type: 'rate', ...rate } as any}
          room={{ type: 'room', id: bookedRoomId } as any}
          onInlineStepChange={setIsPackageInlineStep}
        />
      )}
    </>
  );
};
